import {FC, ReactNode, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {selectPendingHostIds} from '~/common/fabric/reducers/eviteEvent/selectors';
import {pluralizeWord, prettyFormatPhoneNumber} from '~/common/utils';
import {isDraft} from '~/common/utils/event_utils';
import {Label} from '~/ViewInvitation/common/Label';
import {isHost} from '~/ViewInvitation/utils';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {CrownIcon} from '~library/svg/v2/CrownIcon';
import {PhoneIcon} from '~library/svg/v2/PhoneIcon';
import {UserIcon} from '~library/svg/v2/UserIcon';

type GuestOfHonor = {name: string; email: string} | {name: string; userId: string};

interface HostDetailsProps {
  hostName: string;
  cohost: {
    name: string;
    email: string;
  };
  hostIds: string[];
  phoneNumber?: string;
  guestsOfHonor: GuestOfHonor[];
  status: string;
}

export const HostDetails: FC<HostDetailsProps> = ({
  hostName,
  cohost,
  hostIds = [],
  phoneNumber,
  guestsOfHonor = [],
  status,
}) => {
  const pendingHostIds = useSelector(selectPendingHostIds);

  const hostNames: ReactNode = useMemo(() => {
    if (hostIds.length > 1 && pendingHostIds.length === 0 && cohost.email) {
      return `${hostName} & ${cohost.name}`;
    }
    const hasUnsavedCohost = !pendingHostIds.length && cohost?.email && cohost?.name;
    if ((isDraft(status) || isHost()) && (pendingHostIds.length > 0 || hasUnsavedCohost)) {
      return (
        <>
          {hostName}{' '}
          <Typography as="span" variant="paragraph3" color="coin">
            +{hasUnsavedCohost ? 1 : pendingHostIds.length} (Pending)
          </Typography>
        </>
      );
    }
    return hostName;
  }, [hostIds.length, pendingHostIds.length, cohost.email, cohost.name, status, hostName]);

  return (
    <div className="view-invitation__details-module__host-details">
      <div>
        <Label value="Host Details" data-qa-id="hosted-by" />
        <div className="view-invitation__details-module__host-info">
          <Typography
            variant="paragraph2"
            data-qa-id="hostname"
            className="view-invitation__details-module__detail"
          >
            <UserIcon ratio={0.5} />
            {hostNames}
          </Typography>
          {phoneNumber && (
            <Link
              data-qa-id="phone-number"
              href={`tel:1${phoneNumber}`}
              variant="large"
              className="view-invitation__details-module__detail"
            >
              <PhoneIcon ratio={0.5} />
              {prettyFormatPhoneNumber(phoneNumber)}
            </Link>
          )}
        </div>
      </div>
      {guestsOfHonor.length > 0 && (
        <div>
          <Label
            value={`${pluralizeWord('Guest', guestsOfHonor.length)} of Honor`}
            data-qa-id="guests-of-honor"
          />
          <div className="view-invitation__details-module__guests-of-honor">
            {guestsOfHonor.map((guest) => (
              <Typography
                key={'email' in guest ? guest.email : guest.userId}
                variant="paragraph2"
                data-qa-id="guest-of-honor"
                data-qa-value={'email' in guest ? guest.email : guest.userId}
                className="view-invitation__details-module__detail"
              >
                <CrownIcon ratio={0.5} />
                {guest.name}
              </Typography>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
